export * from "./thresholdChecker";
export * from "./questionnaire";
export * from "./appointment";
import { MEASUREMENT_ACTIVITY_TRANSLATIONS, IActivityStepTranslations, ALERT_TYPE, ALERT_TYPE_COLORS, ALERT_TYPE_DESCRIPTIONS, ALERT_TYPE_TITLES, ACTIVITY_CUSTOM_TRANSLATIONS } from "../constants"
import { ActivityName } from "../types"
import { THRESHOLD_TYPE, TThresholdType } from "app/types/alert"

type TAlertTypes = 'red' | 'yellow' | 'green'
type TThresholdTypes = TThresholdType

export const generateTranslationBasedOnMeasurementType = (
    activityName: ActivityName,
    effectiveAlertType: TAlertTypes,
    thresholdType: TThresholdTypes
) => {
    const thresholdDirection = thresholdType === THRESHOLD_TYPE.HIGH || thresholdType === THRESHOLD_TYPE.HIGH_LOW ? 'high' : 'low';

    if (ACTIVITY_CUSTOM_TRANSLATIONS[activityName] && thresholdType === THRESHOLD_TYPE.WEIGHT_VARIATION_PERCENTAGE) {
        return {
            title: ACTIVITY_CUSTOM_TRANSLATIONS[activityName]!.title,
            description: `measurement.${effectiveAlertType}.${thresholdDirection}.description`,
            type: '',
        };
    }

    return {
        title: `measurement.${effectiveAlertType}.${thresholdDirection}.title`,
        description: `measurement.${effectiveAlertType}.${thresholdDirection}.description`,
        type: `vitalParameters.${activityName}.title`,
    };
};

export const alertTypeResult = (
    activityName: ActivityName, 
    alertType?: TAlertTypes,
    thresholdType?: TThresholdTypes
) => {
    const effectiveAlertType: TAlertTypes = (alertType || ALERT_TYPE.GREEN)
    
    let titleKey = ALERT_TYPE_TITLES[effectiveAlertType];
    let descriptionKey = ALERT_TYPE_DESCRIPTIONS[effectiveAlertType];
    let typeKey = `vitalParameters.${activityName}.title`;

    if (effectiveAlertType !== 'green' && thresholdType) {
        const {title, description, type} = generateTranslationBasedOnMeasurementType(activityName, effectiveAlertType, thresholdType)
        titleKey = title
        descriptionKey = description
        typeKey = type
    }

    return {
        title: titleKey,
        description: descriptionKey,
        color: ALERT_TYPE_COLORS[ALERT_TYPE.GREEN],
        type: typeKey
    }
}

export const getTutorialStepTranslations = (activityName: ActivityName): IActivityStepTranslations => {
    return MEASUREMENT_ACTIVITY_TRANSLATIONS[activityName].tutorialStep;
};

export const getBeforeMeasurementStepTranslations = (activityName: ActivityName): IActivityStepTranslations => {
    return MEASUREMENT_ACTIVITY_TRANSLATIONS[activityName].beforeMeasurementStep;
};

export const canManualMeasurement = (activityName: ActivityName): boolean => {
    switch (activityName) {
        case ActivityName.BLOOD_PRESSURE:
            return true;
        case ActivityName.HEART_RATE:
            return true;
        case ActivityName.TEMPERATURE:
            return true;
        case ActivityName.SPO2:
            return true;
        case ActivityName.WEIGHT:
            return true;
        case ActivityName.ECG:
            return false;
        case ActivityName.STEP:
            return true;
        case ActivityName.DYSPNEA:
            return false;
        default:
            return false;
    }
}

export const getVitalParameterName = (activityName: ActivityName): string => {
    const vitalParameterNames: Record<ActivityName, string> = {
        [ActivityName.BLOOD_PRESSURE]: 'vitalParameters.blood_pressure.title',
        [ActivityName.HEART_RATE]: 'vitalParameters.heart_rate.title',
        [ActivityName.TEMPERATURE]: 'vitalParameters.temperature.title',
        [ActivityName.SPO2]: 'vitalParameters.spo2.title',
        [ActivityName.WEIGHT]: 'vitalParameters.weight.title',
        [ActivityName.STEP]: 'vitalParameters.step.title',
        [ActivityName.DYSPNEA]: 'vitalParameters.dyspnea.title',
        [ActivityName.ECG]: 'vitalParameters.ecg.title'
    };
    return vitalParameterNames[activityName] || activityName;
};

export const formatMeasurementValue = (
    activityName: string,
    measurementValues: Record<string, any>,
    t: (key: string) => string
): { value: string | number, unit: string, showUnit: boolean } => {
    if (activityName === ActivityName.BLOOD_PRESSURE && measurementValues.systolic && measurementValues.diastolic) {
        return {
            value: `${measurementValues.systolic}/${measurementValues.diastolic}`,
            unit: measurementValues.unit || 'mmHg',
            showUnit: true
        };
    }

    if (activityName === ActivityName.ECG && measurementValues.hasOwnProperty('afib')) {
        const isIrregular = measurementValues.afib === true;
        return {
            value: t(isIrregular ? 'vitalParameters.ecg.results.irregular' : 'vitalParameters.ecg.results.normal'),
            unit: '',
            showUnit: false
        };
    }

    const value = Object.values(measurementValues).find(val => typeof val === 'number') || '--';
    return {
        value,
        unit: measurementValues.unit || '',
        showUnit: true
    };
};